import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useMemo } from 'react';
import { getUrl } from '@atlaskit/linking-common';
import { chunkloadFailedEvent as _chunkloadFailedEvent, connectFailedEvent as _connectFailedEvent, connectSucceededEvent as _connectSucceededEvent, context, instrumentEvent, invokeFailedEvent as _invokeFailedEvent, invokeSucceededEvent as _invokeSucceededEvent, screenAuthPopupEvent, trackAppAccountAuthStarted, trackAppAccountConnected, uiActionClickedEvent, uiAuthAlternateAccountEvent, uiAuthEvent, uiCardClickedEvent, uiClosedAuthEvent, uiHoverCardDismissedEvent, uiHoverCardOpenLinkClickedEvent, uiHoverCardViewedEvent, uiLearnMoreLinkClickedEvent, uiRenderFailedEvent, uiRenderSuccessEvent, uiSmartLinkStatusLozengeButtonClicked, uiSmartLinkStatusListItemButtonClicked, uiSmartLinkStatusOpenPreviewButtonClicked } from '../../utils/analytics';
import { failUfoExperience, startUfoExperience, succeedUfoExperience } from './ufoExperiences';
import { getDefinitionId, getExtensionKey, getProduct, getResourceType, getStatusDetails, getSubproduct } from '../helpers';
import { useSmartLinkContext } from '@atlaskit/link-provider';
import { trackHoverCardResolutionStarted, trackSmartLinkQuickActionFailed, trackSmartLinkQuickActionStarted, trackSmartLinkQuickActionSuccess, uiIframeDwelledEvent, uiIframeFocusedEvent, uiServerActionClicked } from '../../utils/analytics/analytics';
import { useDispatchAnalytics } from './useDispatchAnalytics';
var applyCommonAttributes = function applyCommonAttributes(event, commonAttributes) {
  if (event && event.attributes) {
    for (var _i = 0, _Object$entries = Object.entries(commonAttributes); _i < _Object$entries.length; _i++) {
      var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
        key = _Object$entries$_i[0],
        value = _Object$entries$_i[1];
      if (event.attributes[key] === undefined) {
        event.attributes[key] = value;
      }
    }
  }
  return event;
};

/**
 * This hook provides usage of Smart Link analytics outside of the Card component.
 * Can be provided to Card via the analyticsEvents prop to change the analytics events.
 *
 * @deprecated {@link https://hello.atlassian.net/browse/ENGHEALTH-2681 Internal documentation for deprecation (no external access)}
 * This hook's external use is deprecated and may no longer be exported from this package in future releases.
 * Please avoid using it in conjunction with the `analyticsEvents` prop.
 *
 * @param url URL of the link
 * @param dispatchAnalytics dispatchAnalytics function
 * @param id fallback id of the events sent if no id is available
 * @param defaultLocation location attribute to be used
 * @returns
 */
export var useSmartLinkAnalytics = function useSmartLinkAnalytics(url, _dispatchAnalytics, id, defaultLocation) {
  var _useDispatchAnalytics = useDispatchAnalytics(_dispatchAnalytics),
    dispatchAnalytics = _useDispatchAnalytics.dispatchAnalytics;
  var defaultId = id || 'NULL';
  // We don't want to trigger a re-render by using useSmartCardState
  var _useSmartLinkContext = useSmartLinkContext(),
    store = _useSmartLinkContext.store;
  var state = store ? getUrl(store, url) : undefined;
  var details = state ? state.details : undefined;
  var extractedDefinitionId = getDefinitionId(details);
  var extractedExtensionKey = getExtensionKey(details);
  var extractedResourceType = getResourceType(details);
  var extractedSubproduct = getSubproduct(details);
  var extractedProduct = getProduct(details);
  var extractedStatusDetails = getStatusDetails(details);
  var commonAttributes = useMemo(function () {
    return {
      id: defaultId,
      definitionId: extractedDefinitionId,
      extensionKey: extractedExtensionKey,
      resourceType: extractedResourceType,
      destinationObjectType: extractedResourceType,
      destinationSubproduct: extractedSubproduct,
      destinationProduct: extractedProduct,
      location: defaultLocation,
      statusDetails: extractedStatusDetails
    };
  }, [defaultId, extractedDefinitionId, extractedExtensionKey, extractedResourceType, extractedSubproduct, extractedProduct, defaultLocation, extractedStatusDetails]);

  /** Contains all ui analytics events */
  var ui = useMemo(function () {
    return {
      /**
       * This fires an event that represents when a user clicks on the authentication
       * call to action with no current authenticated account. (i.e. Connect to Preview).
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @returns
       */
      authEvent: function authEvent(_ref) {
        var display = _ref.display,
          extensionKey = _ref.extensionKey,
          definitionId = _ref.definitionId,
          resourceType = _ref.resourceType,
          destinationProduct = _ref.destinationProduct,
          destinationSubproduct = _ref.destinationSubproduct,
          location = _ref.location;
        return dispatchAnalytics(applyCommonAttributes(uiAuthEvent({
          display: display,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        }), commonAttributes));
      },
      /**
       * This fires an event that represents when a user clicks on the authentication
       * call to action with a forbidden authenticated account. (i.e. Try another account).
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @returns
       */
      authAlternateAccountEvent: function authAlternateAccountEvent(_ref2) {
        var display = _ref2.display,
          extensionKey = _ref2.extensionKey,
          definitionId = _ref2.definitionId,
          resourceType = _ref2.resourceType,
          destinationProduct = _ref2.destinationProduct,
          destinationSubproduct = _ref2.destinationSubproduct,
          location = _ref2.location;
        return dispatchAnalytics(applyCommonAttributes(uiAuthAlternateAccountEvent({
          display: display,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        }), commonAttributes));
      },
      /**
       * This fires an event that represents when a user
       * click a button.
       * @param data A partial analytics event payload
       */
      buttonClickedEvent: function buttonClickedEvent(data) {
        return dispatchAnalytics(applyCommonAttributes({
          action: 'clicked',
          actionSubject: 'button',
          actionSubjectId: data.actionSubjectId,
          attributes: _objectSpread(_objectSpread({}, context), data.attributes),
          eventType: 'ui'
        }, commonAttributes));
      },
      /**
       * This fires an event that represents when a user clicks on a Smart Link.
       * @param id The unique ID for this Smart Link.
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       * @param status What status the Smart Link is currently in (e.g. resolved, unresolved)
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param isModifierKeyPressed Whether a modifier key was pressed when clicking the Smart Link.
       * @param location Where the Smart Link is currently rendered.
       * @param destinationProduct The product the Smart Link is linked to.
       * @returns
       */
      cardClickedEvent: function cardClickedEvent(_ref3) {
        var id = _ref3.id,
          display = _ref3.display,
          status = _ref3.status,
          definitionId = _ref3.definitionId,
          extensionKey = _ref3.extensionKey,
          isModifierKeyPressed = _ref3.isModifierKeyPressed,
          location = _ref3.location,
          destinationProduct = _ref3.destinationProduct,
          destinationSubproduct = _ref3.destinationSubproduct,
          actionSubjectId = _ref3.actionSubjectId;
        return dispatchAnalytics(applyCommonAttributes(uiCardClickedEvent({
          id: id,
          display: display,
          status: status,
          definitionId: definitionId,
          extensionKey: extensionKey,
          isModifierKeyPressed: isModifierKeyPressed,
          location: location,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          actionSubjectId: actionSubjectId
        }), commonAttributes));
      },
      /**
       * This fires an event that represents when a user dwells their cursor on a Smart Link's iframe.
       * @param id The unique ID for this Smart Link.
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       * @param status What status the Smart Link is currently in (e.g. resolved, unresolved)
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param location Where the Smart Link is currently rendered.
       * @param destinationProduct The product the Smart Link is linked to.
       * @param dwellTime Total seconds that the user has dwelled on this iframe
       * @param dwellPercentVisible Percentage element was visible at end of dwell
       * @returns
       */
      iframeDwelledEvent: function iframeDwelledEvent(_ref4) {
        var id = _ref4.id,
          display = _ref4.display,
          status = _ref4.status,
          definitionId = _ref4.definitionId,
          extensionKey = _ref4.extensionKey,
          location = _ref4.location,
          destinationProduct = _ref4.destinationProduct,
          destinationSubproduct = _ref4.destinationSubproduct,
          dwellTime = _ref4.dwellTime,
          dwellPercentVisible = _ref4.dwellPercentVisible;
        return dispatchAnalytics(applyCommonAttributes(uiIframeDwelledEvent({
          id: id,
          display: display,
          status: status,
          definitionId: definitionId,
          extensionKey: extensionKey,
          location: location,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          dwellTime: dwellTime,
          dwellPercentVisible: dwellPercentVisible
        }), commonAttributes));
      },
      /**
       * This fires an event that represents when a user clicks or tabs into a Smart Link's iframe.
       * @param id The unique ID for this Smart Link.
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       * @param status What status the Smart Link is currently in (e.g. resolved, unresolved)
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param location Where the Smart Link is currently rendered.
       * @param destinationProduct The product the Smart Link is linked to.
       * @returns
       */
      iframeFocusedEvent: function iframeFocusedEvent(_ref5) {
        var id = _ref5.id,
          display = _ref5.display,
          status = _ref5.status,
          definitionId = _ref5.definitionId,
          extensionKey = _ref5.extensionKey,
          location = _ref5.location,
          destinationProduct = _ref5.destinationProduct,
          destinationSubproduct = _ref5.destinationSubproduct;
        return dispatchAnalytics(applyCommonAttributes(uiIframeFocusedEvent({
          id: id,
          display: display,
          status: status,
          definitionId: definitionId,
          extensionKey: extensionKey,
          location: location,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct
        }), commonAttributes));
      },
      /**
       * This fires an event that represents when a user clicks on a Smart Link action.
       * Note: This also starts the UFO smart-link-action-invocation experience.
       * @param id The unique ID for this Smart Link.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param actionType The type of the action that was clicked, e.g. PreviewAction
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       * @param invokeType @deprecated {@link https://hello.atlassian.net/browse/ENGHEALTH-2681 Internal documentation for deprecation (no external access)}
       * @returns
       */
      actionClickedEvent: function actionClickedEvent(_ref6) {
        var id = _ref6.id,
          actionType = _ref6.actionType,
          display = _ref6.display,
          overrideExtensionKey = _ref6.extensionKey,
          definitionId = _ref6.definitionId,
          resourceType = _ref6.resourceType,
          destinationProduct = _ref6.destinationProduct,
          destinationSubproduct = _ref6.destinationSubproduct,
          location = _ref6.location;
        var extensionKey = overrideExtensionKey !== null && overrideExtensionKey !== void 0 ? overrideExtensionKey : extractedExtensionKey;
        dispatchAnalytics(applyCommonAttributes(uiActionClickedEvent({
          id: id !== null && id !== void 0 ? id : defaultId,
          actionType: actionType,
          display: display,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        }), commonAttributes));
      },
      /**
       * This fires an event that represents when a user clicks on a hover preview's "navigate to link" button.
       * https://product-fabric.atlassian.net/wiki/spaces/EM/pages/3206743323/Analytics+Metrics+-+Hover+Previews
       * @param previewDisplay What format the preview is in.
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param previewInvokeMethod How the preview was triggered.
       */
      hoverCardOpenLinkClickedEvent: function hoverCardOpenLinkClickedEvent(_ref7) {
        var previewDisplay = _ref7.previewDisplay,
          definitionId = _ref7.definitionId,
          extensionKey = _ref7.extensionKey,
          destinationProduct = _ref7.destinationProduct,
          destinationSubproduct = _ref7.destinationSubproduct,
          location = _ref7.location,
          previewInvokeMethod = _ref7.previewInvokeMethod;
        dispatchAnalytics(applyCommonAttributes(uiHoverCardOpenLinkClickedEvent({
          id: defaultId,
          previewDisplay: previewDisplay,
          definitionId: definitionId,
          extensionKey: extensionKey,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location,
          previewInvokeMethod: previewInvokeMethod
        }), commonAttributes));
      },
      /**
       * This fires an event that represents when a user closed the authentication window without authenticating after opening it.
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @returns
       */
      closedAuthEvent: function closedAuthEvent(_ref8) {
        var display = _ref8.display,
          extensionKey = _ref8.extensionKey,
          definitionId = _ref8.definitionId,
          resourceType = _ref8.resourceType,
          destinationProduct = _ref8.destinationProduct,
          destinationSubproduct = _ref8.destinationSubproduct,
          location = _ref8.location;
        return dispatchAnalytics(applyCommonAttributes(uiClosedAuthEvent({
          display: display,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        }), commonAttributes));
      },
      /**
       * This fires an event that represents when a user close a modal.
       * @param data A partial analytics event payload
       */
      modalClosedEvent: function modalClosedEvent(data) {
        return dispatchAnalytics(applyCommonAttributes({
          action: 'closed',
          actionSubject: 'modal',
          actionSubjectId: data.actionSubjectId,
          attributes: _objectSpread(_objectSpread({}, context), data.attributes),
          eventType: 'ui'
        }, commonAttributes));
      },
      /**
       * This fires an event that represents when a Smart Link was rendered successfully.
       * Note: this fires even if the Smart Link request errored out.
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       * @param id The unique ID for this Smart Link.
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param canBeDatasource An indicator that shows that a smart link can be converted to a datasource
       */
      renderSuccessEvent: function renderSuccessEvent(_ref9) {
        var display = _ref9.display,
          status = _ref9.status,
          id = _ref9.id,
          extensionKey = _ref9.extensionKey,
          definitionId = _ref9.definitionId,
          resourceType = _ref9.resourceType,
          destinationProduct = _ref9.destinationProduct,
          destinationSubproduct = _ref9.destinationSubproduct,
          location = _ref9.location,
          _ref9$canBeDatasource = _ref9.canBeDatasource,
          canBeDatasource = _ref9$canBeDatasource === void 0 ? false : _ref9$canBeDatasource;
        var experienceId = id ? id : defaultId;
        succeedUfoExperience('smart-link-rendered', experienceId, {
          extensionKey: extensionKey,
          display: display
        });

        // UFO will disregard this if authentication experience has not yet been started
        succeedUfoExperience('smart-link-authenticated', experienceId, {
          display: display
        });
        dispatchAnalytics(applyCommonAttributes(uiRenderSuccessEvent({
          display: display,
          status: status,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location,
          canBeDatasource: canBeDatasource
        }), commonAttributes));
      },
      /**
       * This fires an event that represents when a Smart Link renders unsuccessfuly.
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       * @param id The unique ID for this Smart Link.
       * @param error: An error representing why the Smart Link render failed.
       * @param errorInfo: Additional details about the error including the stack trace.
       */
      renderFailedEvent: function renderFailedEvent(_ref10) {
        var display = _ref10.display,
          id = _ref10.id,
          error = _ref10.error,
          errorInfo = _ref10.errorInfo,
          extensionKey = _ref10.extensionKey,
          definitionId = _ref10.definitionId,
          resourceType = _ref10.resourceType,
          destinationProduct = _ref10.destinationProduct,
          destinationSubproduct = _ref10.destinationSubproduct,
          location = _ref10.location;
        var experienceId = id ? id : defaultId;
        // Start and fail the smart-link-rendered experience. If it has already
        // been started nothing happens.
        startUfoExperience('smart-link-rendered', experienceId);
        failUfoExperience('smart-link-rendered', experienceId);
        failUfoExperience('smart-link-authenticated', experienceId);
        dispatchAnalytics(applyCommonAttributes(uiRenderFailedEvent({
          display: display,
          error: error,
          errorInfo: errorInfo,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        }), commonAttributes));
      },
      /**
       * This fires an event that represents a hover preview being opened.
       * @param hoverDisplay Whether the hover preview was displayed as a card or embed.
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param previewInvokeMethod How the preview was triggered.
       * @returns
       */
      hoverCardViewedEvent: function hoverCardViewedEvent(_ref11) {
        var previewDisplay = _ref11.previewDisplay,
          previewInvokeMethod = _ref11.previewInvokeMethod,
          id = _ref11.id,
          extensionKey = _ref11.extensionKey,
          definitionId = _ref11.definitionId,
          resourceType = _ref11.resourceType,
          destinationProduct = _ref11.destinationProduct,
          destinationSubproduct = _ref11.destinationSubproduct,
          location = _ref11.location,
          status = _ref11.status;
        return dispatchAnalytics(applyCommonAttributes(uiHoverCardViewedEvent({
          id: id,
          previewDisplay: previewDisplay,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location,
          previewInvokeMethod: previewInvokeMethod,
          status: status
        }), commonAttributes));
      },
      /**
       * This fires an event that represents a hover preview being dismissed.
       * @param hoverDisplay Whether the hover preview was displayed as a card or embed.
       * @param hoverTime The duration that the user hovered over a Smart Link before the preview was dismissed.
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resolver invoked.
       * @param previewInvokeMethod How the preview was triggered.
       * @returns
       */
      hoverCardDismissedEvent: function hoverCardDismissedEvent(_ref12) {
        var id = _ref12.id,
          previewDisplay = _ref12.previewDisplay,
          hoverTime = _ref12.hoverTime,
          previewInvokeMethod = _ref12.previewInvokeMethod,
          extensionKey = _ref12.extensionKey,
          definitionId = _ref12.definitionId,
          resourceType = _ref12.resourceType,
          destinationProduct = _ref12.destinationProduct,
          destinationSubproduct = _ref12.destinationSubproduct,
          location = _ref12.location,
          status = _ref12.status;
        return dispatchAnalytics(applyCommonAttributes(uiHoverCardDismissedEvent({
          previewDisplay: previewDisplay,
          id: id,
          hoverTime: hoverTime,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location,
          previewInvokeMethod: previewInvokeMethod,
          status: status
        }), commonAttributes));
      },
      /**
       * Fires an event that signifies that a "Learn More" link was clicked on an unauthenticated card
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param location The location where a link is displayed (jiraWebLinks, confluencePages etc)
       */
      learnMoreClickedEvent: function learnMoreClickedEvent() {
        return dispatchAnalytics(applyCommonAttributes(uiLearnMoreLinkClickedEvent(), commonAttributes));
      },
      /**
       * Fires an event that represent a click was performed on a Status Lozenge
       */
      smartLinkLozengeActionClickedEvent: function smartLinkLozengeActionClickedEvent() {
        return dispatchAnalytics(applyCommonAttributes(uiSmartLinkStatusLozengeButtonClicked(), commonAttributes));
      },
      /**
       * Fires an event that represent a click was performed on a Status Lozenge's dropdown item
       */
      smartLinkLozengeActionListItemClickedEvent: function smartLinkLozengeActionListItemClickedEvent() {
        return dispatchAnalytics(applyCommonAttributes(uiSmartLinkStatusListItemButtonClicked(), commonAttributes));
      },
      /**
       * Fires an event that represent a click was performed on a Status Lozenge open preview button
       */
      smartLinkLozengeActionErrorOpenPreviewClickedEvent: function smartLinkLozengeActionErrorOpenPreviewClickedEvent() {
        return dispatchAnalytics(applyCommonAttributes(uiSmartLinkStatusOpenPreviewButtonClicked(), commonAttributes));
      },
      smartLinkServerActionClickedEvent: function smartLinkServerActionClickedEvent(props) {
        return dispatchAnalytics(applyCommonAttributes(uiServerActionClicked(props), commonAttributes));
      }
    };
  }, [dispatchAnalytics, commonAttributes, defaultId, extractedExtensionKey]);

  /** Contains all operational analytics events */
  var operational = useMemo(function () {
    return {
      /**
       * This fires an event that represents an action being successfully invoked.
       * @param id The unique ID for this Smart Link.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param actionType The type of action invoked, e.g. PreviewAction
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       */
      invokeSucceededEvent: function invokeSucceededEvent(_ref13) {
        var id = _ref13.id,
          actionType = _ref13.actionType,
          display = _ref13.display,
          extensionKey = _ref13.extensionKey,
          definitionId = _ref13.definitionId,
          resourceType = _ref13.resourceType,
          destinationProduct = _ref13.destinationProduct,
          destinationSubproduct = _ref13.destinationSubproduct,
          location = _ref13.location;
        dispatchAnalytics(applyCommonAttributes(_invokeSucceededEvent({
          id: id !== null && id !== void 0 ? id : defaultId,
          actionType: actionType,
          display: display,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        }), commonAttributes));
      },
      /**
       * This fires an event that represents an action being unsuccessfully invoked.
       * @param id The unique ID for this Smart Link.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param actionType The type of action invoked, e.g. PreviewAction
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       * @param reason The reason the invocation failed.
       */
      invokeFailedEvent: function invokeFailedEvent(_ref14) {
        var id = _ref14.id,
          actionType = _ref14.actionType,
          display = _ref14.display,
          reason = _ref14.reason,
          extensionKey = _ref14.extensionKey,
          definitionId = _ref14.definitionId,
          resourceType = _ref14.resourceType,
          destinationProduct = _ref14.destinationProduct,
          destinationSubproduct = _ref14.destinationSubproduct,
          location = _ref14.location;
        dispatchAnalytics(applyCommonAttributes(_invokeFailedEvent({
          id: id !== null && id !== void 0 ? id : defaultId,
          actionType: actionType,
          display: display,
          reason: reason,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        }), commonAttributes));
      },
      /**
       * This fires an event that represents an account successfully being connected via a Smart Link.
       * @param id The unique ID for this Smart Link.
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       */
      connectSucceededEvent: function connectSucceededEvent(_ref15) {
        var id = _ref15.id,
          extensionKey = _ref15.extensionKey,
          definitionId = _ref15.definitionId,
          resourceType = _ref15.resourceType,
          destinationProduct = _ref15.destinationProduct,
          destinationSubproduct = _ref15.destinationSubproduct,
          location = _ref15.location;
        var experienceId = id ? id : defaultId;
        startUfoExperience('smart-link-authenticated', experienceId, {
          extensionKey: extensionKey,
          status: 'success'
        });
        dispatchAnalytics(applyCommonAttributes(_connectSucceededEvent(_objectSpread(_objectSpread({}, commonAttributes), {}, {
          id: experienceId,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        })), commonAttributes));
      },
      /**
       * This fires an event that represents an account unsuccessfully being connected.
       * @param id The unique ID for this Smart Link.
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param reason The reason why the Smart Link connect account failed.
       */
      connectFailedEvent: function connectFailedEvent(_ref16) {
        var id = _ref16.id,
          reason = _ref16.reason,
          extensionKey = _ref16.extensionKey,
          definitionId = _ref16.definitionId,
          resourceType = _ref16.resourceType,
          destinationProduct = _ref16.destinationProduct,
          destinationSubproduct = _ref16.destinationSubproduct,
          location = _ref16.location;
        var experienceId = id ? id : defaultId;
        startUfoExperience('smart-link-authenticated', experienceId, {
          extensionKey: extensionKey,
          status: reason
        });
        dispatchAnalytics(applyCommonAttributes(_connectFailedEvent(_objectSpread(_objectSpread({}, commonAttributes), {}, {
          id: experienceId,
          reason: reason,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        })), commonAttributes));
      },
      /**
       * This fires an event which represents a Smart Link request succeeding or failing based on the status.
       * @param id The unique ID for this Smart Link.
       * @param status The status of the Smart Link.
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param resourceType The type of resource that was invoked. This is provider specific (e.g. File, PullRequest).
       * @param error An error representing why the Smart Link request failed.
       */
      instrument: function instrument(_ref17) {
        var id = _ref17.id,
          status = _ref17.status,
          extensionKey = _ref17.extensionKey,
          definitionId = _ref17.definitionId,
          resourceType = _ref17.resourceType,
          destinationProduct = _ref17.destinationProduct,
          destinationSubproduct = _ref17.destinationSubproduct,
          location = _ref17.location,
          error = _ref17.error;
        var event = instrumentEvent(_objectSpread(_objectSpread({}, commonAttributes), {}, {
          id: id,
          status: status,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location,
          error: error
        }));
        if (event) {
          dispatchAnalytics(applyCommonAttributes(event, commonAttributes));
        }
      },
      /**
       * This fires an event that represents when a Smart Link renders unsuccessfuly.
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       * @param id The unique ID for this Smart Link.
       * @param error: An error representing why the Smart Link render failed.
       * @param errorInfo: Additional details about the error including the stack trace.
       */
      chunkloadFailedEvent: function chunkloadFailedEvent(_ref18) {
        var display = _ref18.display,
          error = _ref18.error,
          errorInfo = _ref18.errorInfo,
          extensionKey = _ref18.extensionKey,
          definitionId = _ref18.definitionId,
          resourceType = _ref18.resourceType,
          destinationProduct = _ref18.destinationProduct,
          destinationSubproduct = _ref18.destinationSubproduct,
          location = _ref18.location;
        dispatchAnalytics(applyCommonAttributes(_chunkloadFailedEvent({
          display: display,
          error: error,
          errorInfo: errorInfo,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        }), commonAttributes));
      }
    };
  }, [defaultId, commonAttributes, dispatchAnalytics]);

  /** Contains all track analytics events */
  var track = useMemo(function () {
    return {
      /**
       * This fires an event which represents a user connecting their account to view a Smart Link.
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @returns
       */
      appAccountConnected: function appAccountConnected(_ref19) {
        var extensionKey = _ref19.extensionKey,
          definitionId = _ref19.definitionId,
          resourceType = _ref19.resourceType,
          destinationProduct = _ref19.destinationProduct,
          destinationSubproduct = _ref19.destinationSubproduct,
          location = _ref19.location;
        return dispatchAnalytics(applyCommonAttributes(trackAppAccountConnected(_objectSpread(_objectSpread({}, commonAttributes), {}, {
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        })), commonAttributes));
      },
      /**
       * This fires an event which represents a user starting the Smart Link connect account process.
       * @param location a location where the Smart Link auth action was initiated.
       * @param extensionKey The extensionKey of the Smart Link resolver invoked.
       * @returns
       */
      appAccountAuthStarted: function appAccountAuthStarted(_ref20) {
        var extensionKey = _ref20.extensionKey,
          location = _ref20.location;
        return dispatchAnalytics(applyCommonAttributes(trackAppAccountAuthStarted(_objectSpread(_objectSpread({}, commonAttributes), {}, {
          extensionKey: extensionKey,
          location: location
        })), commonAttributes));
      },
      /**
       * Removed in EDM-5153: This is an even from an experiment.
       * @deprecated
       */
      linkUpdated: function linkUpdated() {},
      /**
       * This fires a tracking event before an action invoke api call is made
       */
      smartLinkQuickActionStarted: function smartLinkQuickActionStarted(props) {
        return dispatchAnalytics(applyCommonAttributes(trackSmartLinkQuickActionStarted(_objectSpread(_objectSpread({}, commonAttributes), props)), commonAttributes));
      },
      /**
       * This fires a tracking event after an action invoke api call is successful
       */
      smartLinkQuickActionSuccess: function smartLinkQuickActionSuccess(props) {
        return dispatchAnalytics(applyCommonAttributes(trackSmartLinkQuickActionSuccess(_objectSpread(_objectSpread({}, commonAttributes), props)), commonAttributes));
      },
      /**
       * This fires a tracking event after an action invoke api call has failed
       */
      smartLinkQuickActionFailed: function smartLinkQuickActionFailed(props) {
        return dispatchAnalytics(applyCommonAttributes(trackSmartLinkQuickActionFailed(_objectSpread(_objectSpread({}, commonAttributes), props)), commonAttributes));
      },
      /**
       * Fires a track event when we call loadMetadata() to get information for a hover card
       */
      hoverCardResolutionStarted: function hoverCardResolutionStarted() {
        return dispatchAnalytics(applyCommonAttributes(trackHoverCardResolutionStarted(_objectSpread({
          display: 'hoverCardPreview'
        }, commonAttributes)), commonAttributes));
      }
    };
  }, [commonAttributes, dispatchAnalytics]);

  /** Contains all screen analytics events */
  var screen = useMemo(function () {
    return {
      /**
       * This fires an event which represents the connect account page being opened.
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @returns
       */
      authPopupEvent: function authPopupEvent(_ref21) {
        var extensionKey = _ref21.extensionKey,
          definitionId = _ref21.definitionId,
          resourceType = _ref21.resourceType,
          destinationProduct = _ref21.destinationProduct,
          destinationSubproduct = _ref21.destinationSubproduct,
          location = _ref21.location;
        return dispatchAnalytics(applyCommonAttributes(screenAuthPopupEvent(_objectSpread(_objectSpread({}, commonAttributes), {}, {
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        })), commonAttributes));
      },
      /**
       * This fires an event that represents when a user view a modal.
       * @param data A partial analytics event payload
       */
      modalViewedEvent: function modalViewedEvent(data) {
        return dispatchAnalytics(applyCommonAttributes({
          action: 'viewed',
          actionSubject: data.name,
          attributes: _objectSpread(_objectSpread({}, context), data.attributes),
          eventType: 'screen',
          name: data.name
        }, commonAttributes));
      }
    };
  }, [commonAttributes, dispatchAnalytics]);
  return useMemo(function () {
    return {
      ui: ui,
      operational: operational,
      track: track,
      screen: screen
    };
  }, [ui, operational, track, screen]);
};